import {
  LocalOffer as CalculationIcon,
  RequestQuote as CostIcon,
  ShoppingCart as ECommerceIcon,
  Home,
  Map as MapIcon,
  PermMedia as MediaIcon,
  Inventory as PackageIcon,
  Person as PersonIcon,
  RequestQuote as QuoteIcon,
  Settings,
  Task as TaskIcon,
} from "@mui/icons-material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import type { ReactElement } from "react";
import { useLocation } from "react-router-dom";
import type { UserContext, UserRole } from "../auth/auth";
import { canAccessCustomers, canAccessStatistics, isAgentWithProductsAndProviderAccess } from "../auth/auth";
import { featureFlags } from "../featureFlags/featureFlags";

const isAdmin = (context: SitemapLinkContext) => context.role === "ADMIN";
const isAdminOrAgent = (context: SitemapLinkContext) => isAdmin(context) || context.role === "AGENT";
const isProvider = (context: SitemapLinkContext) => context.role === "PROVIDER";
const isNotProvider = (context: SitemapLinkContext) => !isProvider(context);

interface SitemapLinkContext {
  readonly role: UserRole | undefined;
  readonly pathname: string;
  readonly canAccessAdmin: boolean;
  readonly canAccessCustomers: boolean;
}

const sitemap: Record<
  string,
  {
    link: string;
    icon: ReactElement;
    enabled(context: SitemapLinkContext): boolean;
  }
> = {
  Zadania: {
    link: "/tasks",
    icon: <TaskIcon />,
    enabled: isAdminOrAgent,
  },
  Zamówienia: {
    link: "/orders",
    icon: <Home />,
    enabled: isNotProvider,
  },
  Kalkulacje: {
    link: "/calculations",
    icon: <CalculationIcon />,
    enabled: isNotProvider,
  },
  "E-commerce": {
    link: "/e-commerce",
    icon: <ECommerceIcon />,
    enabled: isNotProvider,
  },
  Koszty: {
    link: "/costs",
    icon: <CostIcon />,
    enabled: isAdmin,
  },
  Wyceny: {
    link: "/quotes",
    icon: <QuoteIcon />,
    enabled(context) {
      const isQuotePath = /^\/quotes\/.+/.test(context.pathname);
      return (isProvider(context) || isAdminOrAgent(context)) && isQuotePath;
    },
  },
  Pakiety: { link: "/public/packages", icon: <PackageIcon />, enabled: () => true },
  "Media bank": {
    link: "/media",
    icon: <MediaIcon />,
    enabled: isNotProvider,
  },
  "Mapa produktów": {
    link: "/map",
    icon: <MapIcon />,
    enabled: isNotProvider,
  },
  Nagrody: {
    link: "/rewards",
    icon: <EmojiEventsIcon />,
    enabled(context) {
      return isNotProvider(context) && featureFlags.enabled("loyalty-points");
    },
  },
  Klienci: {
    link: "/customers",
    icon: <PersonIcon />,
    enabled(context) {
      return context.canAccessCustomers;
    },
  },
  Admin: {
    link: "/admin",
    icon: <Settings />,
    enabled(context) {
      return context.canAccessAdmin;
    },
  },
};

export function useSiteMap(userContext: UserContext | null): {
  label: string;
  icon: ReactElement;
  link: string;
}[] {
  const { pathname } = useLocation();
  const role = userContext?.role;
  const linkContext: SitemapLinkContext = {
    role,
    pathname,
    canAccessAdmin:
      role === "ADMIN" || isAgentWithProductsAndProviderAccess(userContext) || canAccessStatistics(userContext),
    canAccessCustomers: canAccessCustomers(userContext),
  };

  return Object.entries(sitemap)
    .map(([label, entry]) => ({ label, entry }))
    .filter((it) => it.entry.enabled(linkContext))
    .map((it) => ({
      label: it.label,
      icon: it.entry.icon,
      link: it.entry.link,
    }));
}
